import { Button } from "@fluentui/react-components";
import { Person20Regular } from "@fluentui/react-icons";
import { useIntl } from "react-intl";
import { useAuth } from "@/hooks/authentication";
import { useMutation } from "@tanstack/react-query";
import { useMessage } from "@/common/messages";
import { signOutOtherAgent } from "@/repository/queryFns/supervisor";
import { captureEvent } from "@/common/helpers/sentryHelper";
import { useSentryAppContext } from "@/hooks/sentry";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { useCallback, useContext } from "react";
import { GlobalDialogContext } from "@/components/atoms/dialog/context";
import { useStyle } from "./style";

interface SignOutButtonProps {
	className?: string;
	userId?: string;
	agentName?: string;
	disabled?: boolean;
}

export const SignOutButton = ({
	className,
	userId,
	agentName,
	disabled
}: SignOutButtonProps) => {
	const intl = useIntl();
	const authentication = useAuth();
	const { show: showMessage } = useMessage();
	const { getAppContext } = useSentryAppContext();
	const { showDialog, hideDialog } = useContext(GlobalDialogContext);
	const classes = useStyle();
	const signOutLabel = intl.formatMessage({ id: "Account.SignOut" });

	const signOutMutation = useMutation({
		mutationFn: async (targetUserId?: string) => {
			if (targetUserId) {
				return signOutOtherAgent(targetUserId);
			} else {
				return authentication.signOut();
			}
		},
		onSuccess: () => {
			showMessage("global", {
				text: intl.formatMessage({ id: "Account.SignOutPerformed" }),
				intent: "success",
			});
		},
		onError: () => {
			showMessage("global", {
				text: intl.formatMessage({
					id: userId
						? "Account.SignOutFailed.ForSupervisor"
						: "Account.SignOutFailed.ForAgent",
				}),
				intent: "error",
			});
		},
	});

	const handleSignOut = useCallback(() => {
		captureEvent("Agent sign out action performed", {
			signedOutBySupervisor: !!userId,
			targetUserId: userId || Lockr.get(StorageKeys.UserId),
			...getAppContext(),
		});

		signOutMutation.mutate(userId);
		if (agentName) {
			hideDialog();
		}
	}, [userId, signOutMutation, hideDialog, getAppContext, agentName]);

	const showConfirmationDialog = useCallback(() => {
		const message = intl.formatMessage(
			{ id: "Account.SignOutConfirmation" },
			{ agentName: agentName }
		);

		showDialog(
			signOutLabel,
			<div>
				<p>{message}</p>
				<div className={classes.signOutDialog}>
					<Button appearance="secondary" onClick={hideDialog}>
						{intl.formatMessage({ id: "Common.Cancel" })}
					</Button>
					<Button
						appearance="primary"
						onClick={handleSignOut}
						disabled={signOutMutation.isPending}
					>
						{signOutLabel}
					</Button>
				</div>
			</div>
		);
	}, [
		showDialog,
		hideDialog,
		handleSignOut,
		agentName,
		intl,
		signOutMutation.isPending,
	]);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();

		if (agentName) {
			showConfirmationDialog();
		} else {
			captureEvent("Agent sign out action performed", {
				signedOutBySupervisor: false,
				targetUserId: Lockr.get(StorageKeys.UserId),
				...getAppContext(),
			});

			signOutMutation.mutate(userId);
		}
	};

	return (
		<Button
			appearance="transparent"
			className={className}
			icon={<Person20Regular />}
			children={
				!agentName
					? intl.formatMessage({ id: "Account.SignOut" })
					: intl.formatMessage({ id: "Account.SignOutAgent" })
			}
			disabled={signOutMutation.isPending || disabled}
			onClick={handleClick}
		/>
	);
};
